/**
 * MatchingCards - Duolingo-style matching
 * Single grid with all cards mixed. Tap one, tap another - if they match, both stay. If not, deselect.
 */

import { useState, useMemo } from 'react';
import { cn } from '@/utils/cn';
import { shuffle } from '@/utils/shuffle';
import type { MatchingActivity, MatchingAnswer } from '@/types/story.types';
import styles from '../StoryPage.module.scss';

interface CardItem {
  id: string;
  side: 'left' | 'right';
  value: string;
  rightIndex?: number; // for duplicate right values
}

interface MatchingCardsProps {
  activity: MatchingActivity;
  answer: MatchingAnswer | undefined;
  onChange: (leftItem: string, rightItem: string) => void;
  disabled?: boolean;
  feedback?: { isCorrect: boolean };
}

const correctPairsSet = (pairs: Array<{ left: string; right: string }>) =>
  new Set(pairs.map((p) => `${p.left}::${p.right}`));

export function MatchingCards({
  activity,
  answer,
  onChange,
  disabled = false,
  feedback,
}: MatchingCardsProps) {
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [incorrectIds, setIncorrectIds] = useState<Set<string>>(new Set());
  const [resetIds, setResetIds] = useState<Set<string>>(new Set());

  const correctPairs = useMemo(
    () => correctPairsSet(activity.pairs),
    [activity.pairs]
  );

  const allCards = useMemo(() => {
    const leftCards: CardItem[] = activity.pairs.map((p, i) => ({
      id: `left-${i}`,
      side: 'left' as const,
      value: p.left,
    }));
    const rightCards: CardItem[] = activity.pairs.map((p, i) => ({
      id: `right-${i}`,
      side: 'right' as const,
      value: p.right,
      rightIndex: i,
    }));
    // Mix all cards together and shuffle for fully random order
    const combined = [...leftCards, ...rightCards];
    return shuffle(combined);
  {/* eslint-disable-next-line react-hooks/exhaustive-deps */}
  }, [activity.id]);

  // Only consider a pair matched if right is non-empty (initial state has right:'')
  const matchedPairs = (answer?.pairs ?? []).filter((p) => p.right !== '');
  const matchedLefts = new Set(matchedPairs.map((p) => p.left));
  const matchedRightIndices = new Set(
    matchedPairs.map((p) => {
      const idx = activity.pairs.findIndex(
        (pair) => pair.left === p.left && pair.right === p.right
      );
      return idx;
    }).filter((i) => i >= 0)
  );

  const isCardMatched = (card: CardItem) => {
    if (card.side === 'left') return matchedLefts.has(card.value);
    return card.rightIndex !== undefined && matchedRightIndices.has(card.rightIndex);
  };

  const INCORRECT_DELAY_MS = 600;
  const RESET_DELAY_MS = 150;

  const handleCardClick = (card: CardItem) => {
    if (disabled || feedback || isCardMatched(card)) return;

    setSelectedIds((prev) => {
      const next = new Set(prev);

      if (next.has(card.id)) {
        next.delete(card.id);
        return next;
      }

      next.add(card.id);

      if (next.size === 2) {
        const [id1, id2] = Array.from(next);
        const c1 = allCards.find((c) => c.id === id1)!;
        const c2 = allCards.find((c) => c.id === id2)!;

        // Both from the same side — show incorrect feedback like wrong pair
        if (c1.side === c2.side) {
          const ids = [id1, id2].filter(Boolean) as string[];
          setIncorrectIds(new Set(ids));
          setTimeout(() => {
            setIncorrectIds(new Set());
            setResetIds(new Set(ids));
            setTimeout(() => setResetIds(new Set()), RESET_DELAY_MS);
          }, INCORRECT_DELAY_MS);
          return new Set();
        }

        const leftCard = c1.side === 'left' ? c1 : c2;
        const rightCard = c1.side === 'right' ? c1 : c2;

        const isCorrect = correctPairs.has(`${leftCard.value}::${rightCard.value}`);
        if (isCorrect) {
          onChange(leftCard.value, rightCard.value);
          return new Set();
        } else {
          const ids = [id1, id2].filter(Boolean) as string[];
          setIncorrectIds(new Set(ids));
          setTimeout(() => {
            setIncorrectIds(new Set());
            setResetIds(new Set(ids));
            setTimeout(() => setResetIds(new Set()), RESET_DELAY_MS);
          }, INCORRECT_DELAY_MS);
          return new Set();
        }
      }

      return next;
    });
  };

  return (
    <div className={styles.story__matchingCards}>
      <div className={styles.story__matchingCardsGrid}>
        {allCards.map((card) => {
          const isMatched = isCardMatched(card);
          const isSelected = selectedIds.has(card.id);
          const isIncorrect = incorrectIds.has(card.id);
          const isReset = resetIds.has(card.id);

          return (
            <button
              key={card.id}
              type="button"
              data-card-id={card.id}
              data-no-press-scale=""
              className={cn(
                styles.story__matchingCard,
                isSelected && styles['story__matchingCard--selected'],
                isMatched && styles['story__matchingCard--matched'],
                isIncorrect && styles['story__matchingCard--incorrect'],
                isReset && styles['story__matchingCard--reset']
              )}
              onClick={() => handleCardClick(card)}
              disabled={disabled || !!feedback}
            >
              <span className={styles.story__matchingCardText}>{card.value}</span>
            </button>
          );
        })}
      </div>
    </div>
  );
}
